<template>
  <div>
    <el-form label-position="top" @submit.native.prevent>
      <el-row :gutter="10" class="u-mt-10 u-mb-10">
        <el-col :xs="24" :sm="12" :md="12" v-if="usuarioSemInstituicaoVinculada">
          <el-form-item label="Instituição" key="instituicao">
            <el-select v-model="instituicaoSelected" filterable placeholder="Selecione a instituição" clearable @change="load">
              <el-option v-for="(item, i) in $store.state.instituicoes" :key="i" :label="item.nome" :value="item.id"></el-option>
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :xs="24" :sm="6" :md="6">
          <el-form-item label="Data" key="data">
            <el-date-picker v-model="dataSelected" type="date" format="dd/MM/yyyy" value-format="yyyy-MM-dd" placeholder="Selecione" :clearable="false" @change="load"></el-date-picker>
          </el-form-item>
        </el-col>
        <el-col :xs="24" :sm="6" :md="6" class="u-text-right u-mt-20">
          <el-button type="info" plain icon="el-icon-refresh" @click="load">ATUALIZAR</el-button>
          <el-button class="u-ml-10" type="primary" plain icon="el-icon-printer" @click="baixarPdf" :loading="loadingPdf">PDF</el-button>
        </el-col>
      </el-row>
    </el-form>

    <div v-if="!instituicaoSelected" class="u-text-center u-mt-25">
      <el-alert title="Selecione uma instituição para visualizar a conformidade dietética do dia." type="info" :closable="false" show-icon></el-alert>
    </div>

    <div v-else-if="loading" class="u-text-center u-mt-25">
      <img src="/public/img/loading-bar.gif" />
    </div>

    <div v-else>
      <el-row :gutter="10" class="u-mt-10">
        <el-col :xs="24" :sm="6" :md="6">
          <div class="card kpi" :class="cardapio ? 'kpi--primary' : 'kpi--warning'">
            <div class="kpi__label">Cardápio do dia</div>
            <div class="kpi__value" style="font-size: 14px;">{{ cardapio ? (cardapio.descricao || ('#' + cardapio.id)) : 'Sem cardápio ativo' }}</div>
            <div class="kpi__hint">{{ formatDateBR(dataSelected) }} ({{ rotuloDiaSemana(diaSemana) }})</div>
          </div>
        </el-col>
        <el-col :xs="24" :sm="6" :md="6">
          <div class="card kpi">
            <div class="kpi__label">Itens do cardápio</div>
            <div class="kpi__value">{{ qtdItens }}</div>
            <div class="kpi__hint">Refeições / pratos do dia</div>
          </div>
        </el-col>
        <el-col :xs="24" :sm="6" :md="6">
          <div class="card kpi">
            <div class="kpi__label">Alunos avaliados</div>
            <div class="kpi__value">{{ qtdAvaliados }}</div>
            <div class="kpi__hint">Com restrição cadastrada</div>
          </div>
        </el-col>
        <el-col :xs="24" :sm="6" :md="6">
          <div class="card kpi" :class="conflitos.length > 0 ? 'kpi--danger' : 'kpi--success'">
            <div class="kpi__label">Conflitos detectados</div>
            <div class="kpi__value">{{ conflitos.length }}</div>
            <div class="kpi__hint">{{ conflitos.length > 0 ? 'Revisar antes de servir' : 'Nenhum conflito ✓' }}</div>
          </div>
        </el-col>
      </el-row>

      <c-panel title="Conflitos detectados" titleIcon="fas fa-exclamation-triangle" class="u-mt-15" v-if="conflitos.length > 0">
        <div class="container-table">
          <table class="table table-striped">
            <thead>
              <tr>
                <th style="width: 56px;"></th>
                <th>Aluno</th>
                <th style="width: 110px;">Tipo</th>
                <th style="width: 200px;">Termo de risco</th>
                <th style="width: 110px;">Origem</th>
                <th>Texto do cardápio</th>
                <th style="width: 100px;" class="u-text-center">Ação</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(linha, idx) in linhasConflitos" :key="idx">
                <!--
                  MER 28 — quem serve a merenda precisa reconhecer o aluno, não só ler o nome.
                  A imagem vem pela rota autenticada; a tela não conhece o caminho do arquivo.
                -->
                <td>
                  <foto-pessoa endpoint="api/merenda/aluno-foto" :id="linha.aluno_id"
                               :nome="linha.aluno_nome" :tamanho="40"></foto-pessoa>
                </td>
                <td>
                  <router-link :to="'/admin/aluno/ficha-saude/' + linha.aluno_id" target="_blank" class="link-aluno">
                    {{ linha.aluno_nome || ('Aluno #' + linha.aluno_id) }}
                  </router-link>
                </td>
                <td>
                  <el-tag size="mini" :type="tipoTagType(linha.tipo_risco)">{{ rotuloTipo(linha.tipo_risco) }}</el-tag>
                </td>
                <td><strong>{{ linha.termo_risco }}</strong></td>
                <td>{{ rotuloOrigem(linha.origem_alvo) }}</td>
                <td class="text-truncate">{{ linha.texto_alvo }}</td>
                <td class="u-text-center">
                  <router-link v-if="cardapio" :to="'/admin/merenda/cardapio/edit/' + cardapio.id" class="link-icon">
                    <el-button type="info" title="Editar cardápio" icon="el-icon-edit" size="mini" plain></el-button>
                  </router-link>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </c-panel>

      <c-panel title="Alunos com restrição sem conflito hoje" titleIcon="fas fa-user-check" class="u-mt-15" v-if="alunosSeguros.length > 0">
        <el-collapse v-model="seguroAberto">
          <el-collapse-item :title="`${alunosSeguros.length} aluno(s) com restrição cadastrada — sem conflito identificado para esta data`" name="seguros">
            <div class="container-table">
              <table class="table table-striped">
                <thead>
                  <tr>
                    <th style="width: 56px;"></th>
                    <th>Aluno</th>
                    <th style="width: 250px;">Restrições cadastradas</th>
                    <th style="width: 100px;" class="u-text-center">Ficha</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="aluno in alunosSeguros" :key="aluno.aluno_id">
                    <td>
                      <foto-pessoa endpoint="api/merenda/aluno-foto" :id="aluno.aluno_id"
                                   :nome="aluno.aluno_nome" :tamanho="40"></foto-pessoa>
                    </td>
                    <td>{{ aluno.aluno_nome || ('Aluno #' + aluno.aluno_id) }}</td>
                    <td>
                      <el-tag v-if="aluno.tem_alergia" size="mini" type="danger" class="u-mr-5">ALERGIA</el-tag>
                      <el-tag v-if="aluno.tem_restricao" size="mini" type="warning" class="u-mr-5">RESTRIÇÃO</el-tag>
                      <el-tag v-if="aluno.diabetes" size="mini" class="u-mr-5">DIABETES</el-tag>
                    </td>
                    <td class="u-text-center">
                      <router-link :to="'/admin/aluno/ficha-saude/' + aluno.aluno_id" target="_blank" class="link-icon">
                        <el-button type="info" icon="el-icon-document" size="mini" plain title="Abrir ficha"></el-button>
                      </router-link>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </el-collapse-item>
        </el-collapse>
      </c-panel>

      <el-alert v-if="qtdAvaliados === 0" class="u-mt-15"
        title="Nenhum aluno com restrição alimentar cadastrada nesta instituição"
        description="Cadastre alergias / restrições / diabetes na ficha de saúde dos alunos para ativar a verificação automática."
        type="info" show-icon :closable="false"></el-alert>
    </div>
  </div>
</template>

<script>
export default {
  props: ['paramInstituicaoId', 'paramData'],
  components: {
    cPanel: () => import('@/shared/c-panel/c-panel.vue'),
    FotoPessoa: () => import('@/shared/foto-pessoa/foto-pessoa.vue')
  },
  data: () => ({
    loading: false,
    loadingPdf: false,
    instituicaoSelected: '',
    dataSelected: '',
    cardapio: null,
    diaSemana: null,
    qtdItens: 0,
    qtdAvaliados: 0,
    conflitos: [],
    alunosSeguros: [],
    seguroAberto: []
  }),
  computed: {
    usuarioSemInstituicaoVinculada () {
      const t = Number(this.$store.state.user.tipo)
      return [4, 5, 6, 7, 8, 10].includes(t)
    },
    linhasConflitos () {
      const out = []
      for (const c of this.conflitos) {
        const matches = c.matches && c.matches.length ? c.matches : [{ tipo_risco: '-', termo_risco: '-', origem_alvo: '-', texto_alvo: '-' }]
        for (const m of matches) {
          out.push({
            aluno_id: c.aluno_id,
            aluno_nome: c.aluno_nome,
            tipo_risco: m.tipo_risco,
            termo_risco: m.termo_risco,
            origem_alvo: m.origem_alvo,
            texto_alvo: m.texto_alvo
          })
        }
      }
      return out
    }
  },
  mounted () {
    this.dataSelected = this.paramData || new Date().toISOString().slice(0, 10)
    this.instituicaoSelected = this.paramInstituicaoId
      ? Number(this.paramInstituicaoId)
      : (this.$store.state.config.instituicao && this.$store.state.config.instituicao.id ? this.$store.state.config.instituicao.id : '')
    if (this.instituicaoSelected) this.load()
  },
  methods: {
    async load () {
      if (!this.instituicaoSelected) {
        window.eventBus.$emit('alert', { msg: 'Selecione uma instituição para continuar.', type: 'warning' })
        return
      }
      this.loading = true
      try {
        const url = `${this.$store.state.config.baseUrlApi}/api/merenda/conformidade-dietetica/${this.instituicaoSelected}`
        const config = Object.assign({}, this.$store.getters.getHeaderAuth, { params: { data: this.dataSelected } })
        const res = await this.$axios.get(url, config)
        const data = res.data || {}
        if (data.erro) {
          window.eventBus.$emit('alert', { msg: typeof data.erro === 'string' ? data.erro : (data.erro.text || 'Erro'), type: 'warning' })
          this.loading = false
          return
        }
        this.cardapio = data.cardapio || null
        this.diaSemana = data.dia_semana
        this.qtdItens = data.qtd_itens_cardapio || 0
        this.qtdAvaliados = data.qtd_alunos_avaliados || 0
        this.conflitos = data.conflitos || []
        this.alunosSeguros = data.alunos_seguros || []
      } catch (e) {
        this.$store.commit('throwException', e)
      }
      this.loading = false
    },
    baixarPdf () {
      if (!this.instituicaoSelected) return
      this.loadingPdf = true
      const url = `${this.$store.state.config.baseUrlApi}/api/merenda/relatorio/dieta_especial/${this.instituicaoSelected}`
      const config = Object.assign({}, this.$store.getters.getHeaderAuthExcel, {
        params: { formato: 'pdf', data_inicio: this.dataSelected, data_fim: this.dataSelected }
      })
      this.$axios.get(url, config)
        .then(response => {
          this.loadingPdf = false
          const blob = new Blob([response.data], { type: 'application/pdf' })
          const link = document.createElement('a')
          link.href = window.URL.createObjectURL(blob)
          link.download = `conformidade_dietetica_${this.dataSelected}.pdf`
          link.click()
          window.URL.revokeObjectURL(link.href)
        })
        .catch(e => { this.loadingPdf = false; this.$store.commit('throwException', e) })
    },
    formatDateBR (s) {
      if (!s) return '-'
      const d = new Date(s + 'T00:00:00')
      return isNaN(d.getTime()) ? s : d.toLocaleDateString('pt-BR')
    },
    rotuloDiaSemana (d) {
      const map = { 0: 'Domingo', 1: 'Segunda', 2: 'Terça', 3: 'Quarta', 4: 'Quinta', 5: 'Sexta', 6: 'Sábado' }
      return map[d] || ''
    },
    rotuloTipo (t) {
      const map = { alergia: 'ALERGIA', restricao: 'RESTRIÇÃO', diabetes: 'DIABETES' }
      return map[t] || (t || '').toUpperCase()
    },
    tipoTagType (t) {
      if (t === 'alergia') return 'danger'
      if (t === 'restricao') return 'warning'
      return ''
    },
    rotuloOrigem (o) {
      const map = { item: 'Item livre', receita: 'Receita', ingrediente: 'Ingrediente' }
      return map[o] || o
    }
  },
  watch: {
    paramInstituicaoId (v) {
      if (v) { this.instituicaoSelected = Number(v); this.load() }
    },
    paramData (v) {
      if (v) { this.dataSelected = v; this.load() }
    }
  }
}
</script>

<style scoped>
.kpi {
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 6px;
  padding: 14px;
  min-height: 92px;
}
.kpi__label { font-size: 11px; color: rgba(0, 0, 0, 0.60); text-transform: uppercase; letter-spacing: 0.4px; }
.kpi__value { font-size: 22px; font-weight: 700; margin-top: 6px; color: #2c3e50; }
.kpi__hint { font-size: 11px; color: rgba(0, 0, 0, 0.55); margin-top: 4px; }
.kpi--primary { border-left: 4px solid #346daa; }
.kpi--success { border-left: 4px solid #50964d; }
.kpi--warning { border-left: 4px solid #E6A23C; }
.kpi--danger { border-left: 4px solid #b22222; background: #fef0f0; }
.text-truncate { max-width: 320px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.link-aluno { color: #346daa; text-decoration: none; }
.link-aluno:hover { text-decoration: underline; }
</style>
